/**
 * Cached graph interface that integrates with the Graph hooks API.
 */
import { toParentKey, useGraphCacheStore, type GraphCacheState } from '@tunasong/graph-cache'
import { includeElement, logger, type FilterFunc } from '@tunasong/models'
import {
  isAudio,
  isPersisted,
  type Audio,
  type ElementType,
  type Entity,
  type EntityType,
  type Persisted,
} from '@tunasong/schemas'
import { useEffect } from 'react'
import { useShallow } from 'zustand/react/shallow'
import type { EntityUpdateFn, GraphHooks, UseChildren } from '../graph-types.js'
import { usePastCollaborators } from '../impl/use-past-collaborators.js'
import { childFilter, pickLatest } from '../util.js'
import { useCurrentUser } from '@tunasong/redux'
import invariant from 'tiny-invariant'

/**
 * The newest of an API candidate and its cache entry, read inside a store selector. Returns one of the existing
 * references, so selectors built on it stay referentially stable across unrelated cache writes. GH #1913
 */
const selectLatest = (state: GraphCacheState, candidate: Persisted<Entity>) =>
  pickLatest(
    candidate,
    state.entityCache.get(candidate.id),
    state.pendingIds.has(candidate.id),
    state.unconfirmed.has(candidate.id)
  ) ?? candidate

/** The cached entries indexed under `parentId` (`null` for roots), without scanning the whole cache */
const selectCachedChildren = (state: GraphCacheState, parentId: string | null) =>
  Array.from(state.childIndex.get(parentId) ?? [], id => state.entityCache.get(id)).filter(e => e !== undefined)

/** Merge API and cached entities, dropping duplicate ids and keeping the latest version of each */
const selectMerged = (
  state: GraphCacheState,
  candidates: Persisted<Entity>[],
  include: (entity: Persisted<Entity>) => boolean
) => {
  const seenIds = new Set<string>()
  const entities: Persisted<Entity>[] = []
  for (const candidate of candidates) {
    if (seenIds.has(candidate.id) || !include(candidate)) {
      continue
    }
    seenIds.add(candidate.id)
    entities.push(selectLatest(state, candidate))
  }
  return entities
}

export const createCachedGraph = <T extends GraphHooks>(graphHooks: T) => {
  const useEntity = (id?: string | null) => {
    const upsertCache = useGraphCacheStore(s => s.upsertCache)
    const status = graphHooks.useEntity(id)

    useEffect(() => {
      if (!(id && status.isSuccess && status.entity)) {
        return
      }
      upsertCache(status.entity)
    }, [id, status.entity, status.isSuccess, upsertCache])

    // Subscribe to this entry only, so writes to other entities do not re-render. GH #1913
    const entity = useGraphCacheStore(s =>
      id ? pickLatest(status.entity, s.entityCache.get(id), s.pendingIds.has(id), s.unconfirmed.has(id)) : undefined
    )

    return { ...status, entity }
  }

  const useEntityUpdate = <E extends Entity = Entity>(props: {
    upsert?: boolean
    debounceDelay?: number
  }): EntityUpdateFn<E> => {
    const apiUpdate = graphHooks.useEntityUpdate(props)
    const optimisticUpdateCache = useGraphCacheStore(s => s.optimisticUpdateCache)
    const getEntry = useGraphCacheStore(s => s.getEntry)

    return (entityId, update) => {
      // Resolve functional updates against the freshest cache entry
      const cacheEntry = getEntry(entityId)
      if (typeof update === 'function' && !cacheEntry) {
        logger.warn('Functional updater called before cache is populated for entity', entityId)
      }
      const resolved = typeof update === 'function' ? update((cacheEntry ?? { id: entityId }) as unknown as E) : update

      /**
       * Update the cache (if it exists) as a pending optimistic change. It keeps the server-issued `updatedAt`:
       * stamping the client clock here let a fast local clock drop newer server and peer versions. GH #1913
       */
      optimisticUpdateCache(entityId, resolved)

      // This will run asynchonously. The cache will be updated with the new entity
      // when the API call is successful in another hook.
      return apiUpdate(entityId, resolved)
    }
  }

  const useEntityChildren = (props: {
    parentId?: string | null
    filter?: EntityType | FilterFunc
    // skip the query
    skip?: boolean
    defaultSort?: boolean
    includeSys?: boolean
    // Poll every x ms
    pollingInterval?: number
  }) => {
    const { parentId, filter, includeSys } = props

    const status = graphHooks.useEntityChildren(props)

    /**
     * Inject the cached children (and newer cached versions) into the result. The parentId index replaces a scan
     * of the whole cache, and `useShallow` re-renders only when an element reference changes. GH #1913
     */
    const isChild = childFilter({ parentId, filter, includeSys })
    const entities = useGraphCacheStore(
      useShallow(s =>
        selectMerged(s, [...status.entities, ...selectCachedChildren(s, toParentKey({ parentId }))], e =>
          Boolean(isChild(e))
        )
      )
    )

    // During RTK Query refetch (e.g., after createEntity cache invalidation),
    // isSuccess temporarily becomes false even though we have cached data.
    // Keep it true during refetch (not initial load) when cached entities are available.
    const isSuccess = status.isSuccess || (status.isFetching && entities.length > 0)

    return {
      ...status,
      entities,
      isSuccess,
    }
  }

  const useEntityCreate = <TEntityType extends Entity>() => {
    const apiCreate = graphHooks.useEntityCreate<TEntityType>()
    const upsertCache = useGraphCacheStore(s => s.upsertCache)
    const { userId } = useCurrentUser()

    /**
     * `optimistic` (default true): when the entity carries a client-generated id, resolve at once with a
     * temporary entity while the server create runs in the background. Pass `optimistic: false` when the
     * caller opens the entity (e.g., navigates into its editor) — the editor's collab/presence room
     * registration is ACL-checked against the stored entity, so it must not run before the create lands.
     */
    const createFn = async <TCreate extends Entity = TEntityType>(props: {
      entity: TCreate
      parent: Persisted<Entity> | null
      isPrivate?: boolean
      optimistic?: boolean
    }) => {
      const { optimistic = true, ...createProps } = props
      const { entity } = createProps

      const resultPromise = apiCreate.createEntity(createProps)

      if (entity.id && optimistic) {
        const temporaryEntity = {
          ...entity,
          userId,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        } as unknown as Persisted<TCreate>
        // If the entity has an ID, we cache it immediately update it
        upsertCache(temporaryEntity)
        // Immediately return the temporary entity
        invariant(isPersisted(temporaryEntity), 'Temporary entity must be persisted')
        return temporaryEntity
      }
      const result = await resultPromise
      if (result) {
        // Through the store, so subscribers and the child index see it
        upsertCache(result)
      }

      return result
    }

    return {
      ...apiCreate,
      createEntity: createFn,
    }
  }

  const useEntitiesById = (ids: string[]) => {
    const status = graphHooks.useEntitiesById(ids)
    const upsertCache = useGraphCacheStore(s => s.upsertCache)

    useEffect(() => {
      if (!status.isSuccess) {
        return
      }
      for (const entity of status.entities) {
        upsertCache(entity)
      }
    }, [status.entities, status.isSuccess, upsertCache])

    const entities = useGraphCacheStore(useShallow(s => status.entities.map(entity => selectLatest(s, entity))))
    const existingEntities = useGraphCacheStore(
      useShallow(s => status.existingEntities.map(entity => selectLatest(s, entity)))
    )

    return {
      ...status,
      entities,
      existingEntities,
    }
  }

  const useEntityTrash = (entity?: Persisted<Entity>) => {
    const update = useEntityUpdate({ debounceDelay: 0 })

    const trashFn = () => {
      if (!entity) {
        return
      }

      /** Update will also update the cache, as a pending optimistic change */
      update(entity.id, { trash: true })
    }
    return trashFn
  }

  const useEntityDelete = () => {
    const [deleteEntity, deleteEntityResult] = graphHooks.useEntityDelete()
    const getEntry = useGraphCacheStore(s => s.getEntry)
    const removeEntry = useGraphCacheStore(s => s.removeEntry)
    const deleteFn = async ({ entity }: { entity: Persisted<Entity> }) => {
      // Update the cache optimistically, through the store so subscribers see it
      if (getEntry(entity.id)) {
        removeEntry(entity.id)
      }
      return deleteEntity({ entity })
    }

    return [deleteFn, deleteEntityResult]
  }

  const useProfiles = (
    props: {
      userIds?: string[]
      includeAssistant?: boolean
    } = {}
  ) => {
    const status = graphHooks.useProfiles(props)
    return status
  }

  const useMembers = (entity?: Persisted<Entity>) => {
    const memberIds = [entity?.userId, ...(entity?.acls?.map(acl => acl.principal) ?? [])].filter(Boolean)

    const status = useProfiles({
      userIds: memberIds,
      includeAssistant: false,
    })
    return {
      ...status,
      members: status.profiles,
    }
  }

  const useEntityChild = <T extends Entity>(props: UseChildren) => {
    const status = graphHooks.useEntityChild<T>(props)

    return status
  }

  const useEntities = <T extends Entity>(props?: {
    /** Use only root entities, i.e., entities that have no parents */
    root?: boolean
    /** Load only partial */
    partial?: boolean
    showTrash?: boolean
    filter?: ElementType | FilterFunc
    /** Load owned entities */
    owned?: boolean
    // polling interval (in ms)
    pollingInterval?: number
  }) => {
    const status = graphHooks.useEntities<T>(props)

    const upsertCacheEntries = useGraphCacheStore(s => s.upsertCacheEntries)

    useEffect(() => {
      if (!status.isSuccess) {
        return
      }
      upsertCacheEntries(status.entities)
    }, [status.entities, status.isSuccess, upsertCacheEntries])

    // Merge in the cached root entities (indexed under `null`), subscribing through a shallow selector. GH #1913
    const entities = useGraphCacheStore(
      useShallow(s => {
        const cachedRootEntities = selectCachedChildren(s, null).filter(e => includeElement(e, props?.filter))
        return selectMerged(s, [...status.entities, ...cachedRootEntities], () => true).filter(
          latest => props?.showTrash || !latest.trash
        )
      })
    )

    return { ...status, entities }
  }

  const useEntityClone = () => {
    const { cloneEntity, cloneEntityStatus } = graphHooks.useEntityClone()
    const upsertCache = useGraphCacheStore(s => s.upsertCache)

    const clone = async <T extends Persisted<Entity> = Persisted<Entity>>(props: {
      entity: T
      updatedProps: Partial<T>
    }) => {
      const result = await cloneEntity(props)
      if (result) {
        upsertCache(result)
      }
      return result
    }

    return { cloneEntity: clone, cloneEntityStatus }
  }

  /** Higher-order convenience hooks */
  const useEntityClips = (sourceId: string) => {
    const { edgeEntities, isLoading: isLoadingEdges } = graphHooks.useEdgeEntities<Entity, Audio>({
      sourceId,
      relation: 'hasRecording',
    })
    const { entities: childClips, isLoading } = graphHooks.useEntityChildren<Audio>({
      parentId: sourceId,
      filter: isAudio,
      includeSys: true,
    })
    const relatedClips = [...childClips, ...edgeEntities.map(({ target }) => target)]

    return { entityClips: relatedClips, isLoading: isLoading || isLoadingEdges }
  }

  return {
    ...graphHooks,
    useEntityClone,
    useEntityCreate,
    useEntity,
    useEntityUpdate,
    useEntityChildren,
    useEntitiesById,
    useMembers,
    useEntityChild,
    useEntityTrash,
    useEntityDelete,
    useEntities,
    usePastCollaborators,
    /** HOC */
    useEntityClips,
  }
}
